import {
  QueryKeys,
  assertParameterIsNotUndefinedOrNull,
  profilesApi,
  useAuthMutation,
} from '@sunrise-ui/api-client';
import {
  type UpdateProfileInput,
  type UpdateProfileLinksInput,
} from '@sunrise-ui/api/profile';
import {
  getFileFromImage,
  getFileFromUri,
  useAuthStore,
  useSnackbar,
} from '@sunrise-ui/primitives';
import { useQueryClient } from '@tanstack/react-query';
import { type ImagePickerAsset } from 'expo-image-picker';

const { profileKey } = QueryKeys.profiles;

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    async mutationFn(data: Omit<UpdateProfileInput, 'user_id'>) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return profilesApi.updateProfileProfilesPut({
        updateProfileInput: { user_id: currentUserId, ...data },
      });
    },
    async onSuccess() {
      const { currentUserId } = useAuthStore.getState();
      await queryClient.invalidateQueries({
        queryKey: [profileKey, currentUserId],
      });
    },
    onError() {
      showSnackbar({
        text: 'Failed to update. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useUpdateProfileLinks = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    async mutationFn(data: UpdateProfileLinksInput) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      return profilesApi.updateProfileLinksProfilesUsersUserIdLinksPut({
        userId: currentUserId,
        updateProfileLinksInput: data,
      });
    },
    async onSuccess() {
      const { currentUserId } = useAuthStore.getState();
      await queryClient.invalidateQueries({
        queryKey: [profileKey, currentUserId],
      });
    },
    onError() {
      showSnackbar({
        text: 'Failed to update. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useUpdateProfilePhoto = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    async mutationFn(image: ImagePickerAsset) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      const file = (await getFileFromImage(image, currentUserId)) as File;
      return profilesApi.updatePhotoProfilesUsersUserIdPhotosPut({
        userId: currentUserId,
        file,
      });
    },
    async onSuccess() {
      const { currentUserId } = useAuthStore.getState();
      await queryClient.invalidateQueries({
        queryKey: [profileKey, currentUserId],
      });
    },
    onError() {
      showSnackbar({
        text: 'Failed to upload. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useUpdateProfileVideo = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useAuthMutation({
    async mutationFn(uri: string) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      const file = await getFileFromUri(uri, currentUserId);
      return profilesApi.updateVideoProfilesUsersUserIdVideosPut({
        userId: currentUserId,
        file,
      });
    },
    async onSuccess() {
      const { currentUserId } = useAuthStore.getState();
      await queryClient.invalidateQueries({
        queryKey: [profileKey, currentUserId],
      });
    },
    onError() {
      showSnackbar({
        text: 'Failed to upload. Please try again.',
        type: 'error',
      });
    },
  });
};
